// Admin: openstaande uitnodigingen + nieuwe versturen. PRD-01 §3.1 US-AUTH-04.

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";

interface Invite {
  id: string;
  email: string;
  role: "client" | "counselor" | "municipality" | "admin";
  expiresAt: string;
  createdAt: string;
}

interface CreatedInvite {
  id: string;
  email: string;
  expiresAt: string;
  /** Activatielink. Altijd aanwezig, ook als de mail wél bezorgd is. */
  inviteUrl: string;
  /** Of de uitnodigingsmail daadwerkelijk de deur uit ging. */
  mailDelivered: boolean;
  mailReason: string | null;
}

const ROLES: Invite["role"][] = ["client", "counselor", "municipality", "admin"];

export function AdminInvitesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const invitesQuery = useQuery({
    queryKey: ["invites"],
    queryFn: () => api.get<{ invites: Invite[] }>("/v1/invites"),
  });

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Invite["role"]>("counselor");
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedInvite | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: () => api.post<CreatedInvite>("/v1/invites", { email, role }),
    onSuccess: (data) => {
      setCreated(data);
      setCopied(false);
      setEmail("");
      setErrorKey(null);
      qc.invalidateQueries({ queryKey: ["invites"] });
    },
    onError: (err) => {
      setCreated(null);
      setErrorKey(err instanceof ApiError ? err.i18nKey : "errors.internal");
    },
  });

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard geweigerd (geen https of geen permissie) — de link staat
      // zichtbaar in het veld, dus handmatig selecteren werkt altijd nog.
      setCopied(false);
    }
  }

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete<{ revoked: boolean }>(`/v1/invites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invites"] }),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setCreated(null);
    setErrorKey(null);
    create.mutate();
  }

  return (
    <AdminLayout title={t("admin.invites.title", "Uitnodigingen")}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-1 bg-white rounded-xl border border-neutral-200 p-5">
          <h3 className="font-semibold mb-4">{t("admin.invites.new", "Nieuwe uitnodiging")}</h3>
          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <label htmlFor="inv-email" className="block text-sm font-medium text-neutral-700 mb-1">
                {t("admin.invites.emailField", "E-mail")}
              </label>
              <input
                id="inv-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
              />
            </div>
            <div>
              <label htmlFor="inv-role" className="block text-sm font-medium text-neutral-700 mb-1">
                {t("admin.invites.roleField", "Rol")}
              </label>
              <select
                id="inv-role"
                value={role}
                onChange={(e) => setRole(e.target.value as Invite["role"])}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-white"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            {created && (
              <div
                className={
                  created.mailDelivered
                    ? "px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800 space-y-2"
                    : "px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 space-y-2"
                }
              >
                <p>
                  {created.mailDelivered
                    ? t("admin.invites.created", "Uitnodiging verstuurd naar {{email}}", {
                        email: created.email,
                      })
                    : t(
                        "admin.invites.createdNoMail",
                        "Uitnodiging aangemaakt voor {{email}}, maar er is géén e-mail verstuurd. Deel de link hieronder zelf.",
                        { email: created.email }
                      )}
                </p>
                <label htmlFor="inv-url" className="block text-xs font-medium">
                  {t("admin.invites.link", "Activatielink (72 uur geldig)")}
                </label>
                <div className="flex gap-2">
                  <input
                    id="inv-url"
                    readOnly
                    value={created.inviteUrl}
                    onFocus={(e) => e.currentTarget.select()}
                    className="flex-1 px-2 py-1 text-xs font-mono bg-white border border-neutral-300 rounded"
                  />
                  <button
                    type="button"
                    onClick={() => void copyLink(created.inviteUrl)}
                    className="px-2 py-1 text-xs font-medium bg-white border border-neutral-300 rounded hover:bg-neutral-50"
                  >
                    {copied ? t("admin.invites.copied", "Gekopieerd") : t("copy", "Kopieer")}
                  </button>
                </div>
              </div>
            )}
            {errorKey && (
              <div role="alert" className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
                {t(errorKey)}
              </div>
            )}

            <button
              type="submit"
              disabled={create.isPending}
              className="w-full px-4 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg"
            >
              {create.isPending
                ? t("admin.invites.sending", "Versturen…")
                : t("admin.invites.send", "Versturen")}
            </button>
          </form>
          <p className="mt-3 text-xs text-neutral-500">
            {t(
              "admin.invites.help",
              "De activatielink is 72 uur geldig. Staat er SMTP ingesteld, dan gaat hij per e-mail naar de ontvanger; anders verschijnt hij hier om zelf te delen."
            )}
          </p>
        </section>

        <section className="lg:col-span-2 bg-white rounded-xl border border-neutral-200">
          <header className="px-5 py-4 border-b border-neutral-200">
            <h3 className="font-semibold">{t("admin.invites.open", "Openstaande uitnodigingen")}</h3>
          </header>
          {invitesQuery.isLoading ? (
            <p className="p-5 text-sm text-neutral-500">{t("loading", "Laden…")}</p>
          ) : invitesQuery.data?.invites.length === 0 ? (
            <p className="p-5 text-sm text-neutral-500">
              {t("admin.invites.empty", "Geen openstaande uitnodigingen.")}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600">
                <tr>
                  <th className="text-left px-5 py-2 font-medium">{t("admin.invites.col.email", "E-mail")}</th>
                  <th className="text-left px-5 py-2 font-medium">{t("admin.invites.col.role", "Rol")}</th>
                  <th className="text-left px-5 py-2 font-medium">{t("admin.invites.col.expires", "Verloopt")}</th>
                  <th className="px-5 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {invitesQuery.data?.invites.map((inv) => (
                  <tr key={inv.id} className="border-t border-neutral-100">
                    <td className="px-5 py-3">{inv.email}</td>
                    <td className="px-5 py-3 text-neutral-600">{inv.role}</td>
                    <td className="px-5 py-3 text-neutral-600">
                      {new Date(inv.expiresAt).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => revoke.mutate(inv.id)}
                        disabled={revoke.isPending}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        {t("admin.invites.revoke", "Intrekken")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
